import React from "react";
import { CircularProgress } from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import {
  DeletePopUp,
  ErrorPanel,
  ErrorPanelHeader,
  isDeletionBlocked,
  LinkedBrokersBlock,
} from "../../../../../components/PopUp";
import { pluralize } from "../../../../../utils";
import { DeleteAccountModalProps, ImpactRow } from "./types";
import {
  ImpactList,
  ImpactItem,
  ImpactIcon,
  ImpactCount,
  SuccessPanel,
  LoadingPanel,
  FootNote,
} from "./styles";

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  accountName,
  onClose,
  onConfirm,
  deletionImpact,
  isLoadingImpact = false,
  isDeleting = false,
  deleteError,
}) => {
  const isBlocked = isDeletionBlocked(deletionImpact);

  const buildImpactRows = (): ImpactRow[] => {
    if (!deletionImpact) {
      return [];
    }

    const {
      expenseCount,
      incomeCount,
      transferCount,
      loanPaymentCount,
      recurringTransactionsCount,
    } = deletionImpact;

    const rows: ImpactRow[] = [
      {
        tone: "expense",
        icon: <TrendingDownIcon />,
        count: expenseCount,
        label: "expense",
      },
      {
        tone: "income",
        icon: <TrendingUpIcon />,
        count: incomeCount,
        label: "income transaction",
      },
      {
        tone: "transfer",
        icon: <SwapHorizIcon />,
        count: transferCount,
        label: "transfer",
      },
      {
        tone: "loan",
        icon: <CreditCardIcon />,
        count: loanPaymentCount,
        label: "loan payment",
      },
      {
        tone: "recurring",
        icon: <AutorenewIcon />,
        count: recurringTransactionsCount,
        label: "recurring transaction",
      },
    ];

    return rows.filter((row) => row.count > 0);
  };

  const renderImpactDetails = () => {
    if (isLoadingImpact) {
      return (
        <LoadingPanel
          role="status"
          aria-label="Loading deletion impact information"
        >
          <CircularProgress size={16} aria-label="Loading" />
          Loading deletion impact…
        </LoadingPanel>
      );
    }

    if (!deletionImpact) {
      return null;
    }

    if (isBlocked) {
      return <LinkedBrokersBlock brokers={deletionImpact.linkedBrokers} />;
    }

    const { totalTransactions, recurringTransactionsCount } = deletionImpact;
    const affectedCount = totalTransactions + recurringTransactionsCount;

    if (affectedCount === 0) {
      return (
        <SuccessPanel role="status" aria-label="No transactions affected">
          <CheckCircleRoundedIcon aria-hidden="true" />
          No transactions will be affected by this deletion.
        </SuccessPanel>
      );
    }

    const impactRows = buildImpactRows();
    const headingText = `${affectedCount.toLocaleString()} ${pluralize(affectedCount, "transaction")} will be permanently impacted or deleted:`;

    return (
      <ErrorPanel
        role="alert"
        aria-label={`Warning: ${affectedCount} transactions will be affected`}
      >
        <ErrorPanelHeader>
          <WarningAmberRoundedIcon aria-hidden="true" />
          {headingText}
        </ErrorPanelHeader>
        <ImpactList aria-label="Transaction impact breakdown">
          {impactRows.map((row) => (
            <ImpactItem key={row.tone}>
              <ImpactIcon $tone={row.tone} aria-hidden="true">
                {row.icon}
              </ImpactIcon>
              <ImpactCount>{row.count.toLocaleString()}</ImpactCount>
              {pluralize(row.count, row.label)}
            </ImpactItem>
          ))}
        </ImpactList>
      </ErrorPanel>
    );
  };

  return (
    <DeletePopUp
      isOpen={isOpen}
      setIsOpen={onClose}
      title="Delete account"
      text={
        <>
          Do you really want to delete the <strong>{accountName}</strong>{" "}
          account?
          {renderImpactDetails()}
          <FootNote>
            <strong>This action cannot be undone.</strong>
          </FootNote>
          <FootNote>
            If you want to preserve the transaction history, consider
            deactivating the account instead.
          </FootNote>
        </>
      }
      handleOnDelete={onConfirm}
      isLoading={isDeleting}
      isConfirmDisabled={isLoadingImpact || isBlocked}
      errorMessage={deleteError}
    />
  );
};
